import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';

const PRIMARY = '#B71530';

const STATUS_CONFIG = {
  failed:    { color: '#dc3545', bg: '#f8d7da', icon: '✕', label: 'Failed'    },
  completed: { color: '#198754', bg: '#d1e7dd', icon: '✓', label: 'Completed' },
  running:   { color: '#0d6efd', bg: '#cfe2ff', icon: '⟳', label: 'Running'   },
  cancelled: { color: '#6c757d', bg: '#e2e3e5', icon: '⊘', label: 'Cancelled' },
};

// Explicit order: failed first so issues are never buried under completed runs
const STATUS_ORDER = ['failed', 'completed', 'running', 'cancelled'];

function duration(start, end) {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  if (ms < 0) return null;
  if (ms < 1000) return '<1s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function fmtDateTime(s) {
  if (!s) return '–';
  return new Date(s).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'Europe/London',
  });
}

function parseErrors(raw) {
  if (!raw) return null;
  // Try JSON first (new format from fixed processor.py)
  try { return JSON.parse(raw); } catch {}
  // Fall back: Python dict repr with single quotes → best-effort parse
  try {
    return JSON.parse(raw.replace(/'/g, '"').replace(/None/g, 'null').replace(/True/g, 'true').replace(/False/g, 'false'));
  } catch {}
  // Return raw as a single "unknown" stage error
  return { pipeline: raw };
}

// ── Stage label map ──────────────────────────────────────────────────────────
const STAGE_LABELS = {
  ingestion:          'Email Ingestion',
  extraction:         'Document Extraction',
  supplier_master:    'Supplier Master Build',
  supplier_matching:  'Supplier Matching',
  duplicates:         'Duplicate Detection',
  reconciliation:     'Reconciliation',
  pipeline:           'Pipeline',
};

function ErrorBlock({ raw }) {
  const errors = parseErrors(raw);
  if (!errors) return null;
  const entries = Object.entries(errors).filter(([, v]) => v);
  if (entries.length === 0) return null;

  return (
    <div style={{ marginTop: '10px' }}>
      {entries.map(([stage, msg]) => (
        <div key={stage} style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #f5c6cb',
          borderLeft: `4px solid #dc3545`,
          borderRadius: '4px',
          padding: '10px 14px',
          marginBottom: '8px',
        }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#dc3545', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
            ✕ {STAGE_LABELS[stage] || stage}
          </div>
          <div style={{ fontSize: '12px', color: '#212529', lineHeight: 1.6, wordBreak: 'break-word', fontFamily: 'system-ui, sans-serif' }}>
            {msg}
          </div>
        </div>
      ))}
    </div>
  );
}

function RunCard({ run, index }) {
  const [expanded, setExpanded] = useState(index === 0 && run.status !== 'completed');
  const cfg = STATUS_CONFIG[run.status] || STATUS_CONFIG.cancelled;
  const dur = duration(run.started_at, run.completed_at);
  const hasError = !!run.error_message;

  return (
    <div className="card mb-3" style={{
      borderLeft: `4px solid ${cfg.color}`,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {/* Header row */}
      <div
        className="card-header d-flex align-items-center justify-content-between py-2"
        style={{ backgroundColor: '#fff', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setExpanded(p => !p)}
      >
        <div className="d-flex align-items-center" style={{ gap: '10px' }}>
          {/* Status badge */}
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '2px 10px', borderRadius: '12px',
            backgroundColor: cfg.bg, color: cfg.color,
            fontSize: '12px', fontWeight: 600,
          }}>
            <span>{cfg.icon}</span> {cfg.label}
          </span>

          <span style={{ fontSize: '13px', color: '#6c757d' }}>
            Run #{run.id}
          </span>

          <span style={{ fontSize: '12px', color: '#495057' }}>
            {fmtDateTime(run.started_at)}
          </span>

          {dur && (
            <span style={{ fontSize: '11px', color: '#adb5bd' }}>
              ({dur})
            </span>
          )}
        </div>

        <div className="d-flex align-items-center" style={{ gap: '12px' }}>
          {/* Quick stats */}
          {[
            { label: 'Emails',    value: run.emails_processed    },
            { label: 'Invoices',  value: run.invoices_extracted  },
            { label: 'Exceptions',value: run.exceptions_raised   },
          ].map(({ label, value }) => (
            <span key={label} style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>
              <span style={{ fontWeight: 600, color: '#212529' }}>{value ?? 0}</span> {label}
            </span>
          ))}

          {hasError && (
            <span style={{ fontSize: '11px', backgroundColor: '#f8d7da', color: '#dc3545', padding: '1px 6px', borderRadius: '10px' }}>
              Has errors
            </span>
          )}

          <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}
            style={{ fontSize: '12px', color: '#6c757d' }} />
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="card-body" style={{ padding: '12px 16px' }}>
          <div className="row mb-3" style={{ gap: 0 }}>
            {[
              { label: 'Status',     value: <span style={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</span> },
              { label: 'Started',    value: fmtDateTime(run.started_at) },
              { label: 'Completed',  value: fmtDateTime(run.completed_at) },
              { label: 'Duration',   value: dur || '–' },
              { label: 'Emails',     value: run.emails_processed ?? 0 },
              { label: 'Documents',  value: run.documents_classified ?? 0 },
              { label: 'Invoices',   value: run.invoices_extracted ?? 0 },
              { label: 'Exceptions', value: run.exceptions_raised ?? 0 },
            ].map(({ label, value }) => (
              <div key={label} className="col-md-3 mb-2">
                <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '2px' }}>
                  {label}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>

          {hasError && <ErrorBlock raw={run.error_message} />}
          {!hasError && run.status === 'completed' && (
            <div style={{ fontSize: '12px', color: '#198754' }}>
              ✓ Pipeline completed successfully with no errors
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AppAdminLogs() {
  const [orgs, setOrgs]           = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [runs, setRuns]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filter, setFilter]       = useState('all');

  useEffect(() => {
    client.get('/orgs/')
      .then(r => {
        const list = r.data.organizations || [];
        setOrgs(list);
        if (list.length > 0) setSelectedOrgId(list[0].id);
      })
      .catch(() => {});
  }, []);

  const fetchRuns = useCallback(() => {
    if (!selectedOrgId) return;
    setLoading(true);
    client.get(`/orgs/${selectedOrgId}/logs?limit=100`)
      .then(r => setRuns(r.data.runs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedOrgId]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const selectedOrg = orgs.find(o => o.id === selectedOrgId);

  const filtered = filter === 'all'
    ? runs
    : runs.filter(r => r.status === filter);

  const counts = runs.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div>
      {/* Org selector */}
      {orgs.length > 0 && (
        <div className="card mb-3" style={{ borderLeft: `4px solid ${PRIMARY}` }}>
          <div className="card-body py-2 d-flex align-items-center" style={{ gap: '12px' }}>
            <i className="bi bi-journal-text" style={{ fontSize: '16px', color: PRIMARY }} />
            <span style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>Viewing org:</span>
            <select
              className="form-control form-control-sm"
              style={{ maxWidth: '280px', fontWeight: 500 }}
              value={selectedOrgId || ''}
              onChange={e => setSelectedOrgId(Number(e.target.value))}
            >
              {orgs.map(o => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.subdomain})
                </option>
              ))}
            </select>
            {selectedOrg && (
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                <code>{selectedOrg.subdomain}.yourdomain.com</code>
              </span>
            )}
            <button className="btn btn-sm btn-outline-secondary ml-auto"
              style={{ fontSize: '12px' }} onClick={fetchRuns} disabled={loading}>
              <i className="bi bi-arrow-clockwise mr-1" />
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Summary stats */}
      {runs.length > 0 && (
        <div className="row mb-3">
          {STATUS_ORDER.map(status => { const cfg = STATUS_CONFIG[status]; return (
            <div className="col-md-3" key={status}>
              <div
                className="card text-center"
                style={{
                  borderLeft: `4px solid ${cfg.color}`,
                  cursor: 'pointer',
                  backgroundColor: filter === status ? cfg.bg : '#fff',
                }}
                onClick={() => setFilter(f => f === status ? 'all' : status)}
              >
                <div className="card-body py-2">
                  <div style={{ fontSize: '22px', fontWeight: 700, color: cfg.color }}>
                    {counts[status] || 0}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>
                    {cfg.label}
                  </div>
                </div>
              </div>
            </div>
          ); })}
        </div>
      )}

      {/* Status filter tabs */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <ul className="nav app-tabs" style={{ marginBottom: 0 }}>
          {[['all', 'All Runs'], ...STATUS_ORDER.map(k => [k, STATUS_CONFIG[k].label])].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                className={`nav-link${filter === key ? ' active' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
                {key !== 'all' && counts[key] ? (
                  <span style={{ marginLeft: '4px', fontSize: '10px', color: filter === key ? '#B71530' : '#6c757d' }}>
                    ({counts[key]})
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          {filtered.length} run{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Run list */}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-danger" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted py-5" style={{ fontSize: '14px' }}>
          <i className="bi bi-journal-x" style={{ fontSize: '36px', display: 'block', marginBottom: '8px' }} />
          No {filter !== 'all' ? filter : ''} runs found for this organisation
        </div>
      ) : (
        filtered.map((run, i) => <RunCard key={run.id} run={run} index={i} />)
      )}
    </div>
  );
}
